import React from 'react';
import { Shield, Plus, Trash2 } from 'lucide-react';

interface Accident {
  date: string;
  fault: string;
  type: string;
  subType?: string;
  policy: string;
  noClaimsAffected: string;
  bodilyInjury: string;
  value: string;
}

interface Conviction {
  offenceDate: string;
  convictionDate: string;
  hasFine: string;
  points: string;
  banLength: string;
  code: string;
}

interface AccidentsConvictionsInfo {
  hasAccidents: string;
  accidents: Accident[];
  hasConvictions: string;
  convictions: Conviction[];
}

interface AccidentsConvictionsStepProps {
  data: AccidentsConvictionsInfo;
  onInputChange: (field: string, value: string | Accident[] | Conviction[]) => void;
}

export function AccidentsConvictionsStep({ data, onInputChange }: AccidentsConvictionsStepProps) {
  const addAccident = () => {
    const newAccident: Accident = {
      date: '',
      fault: '',
      type: '',
      subType: '',
      policy: '',
      noClaimsAffected: '',
      bodilyInjury: '',
      value: ''
    };
    onInputChange('accidents', [...data.accidents, newAccident]);
  };

  const removeAccident = (index: number) => {
    const newAccidents = data.accidents.filter((_, i) => i !== index);
    onInputChange('accidents', newAccidents);
  };

  const updateAccident = (index: number, field: keyof Accident, value: string) => {
    const newAccidents = [...data.accidents];
    newAccidents[index] = { ...newAccidents[index], [field]: value };
    onInputChange('accidents', newAccidents);
  };

  const addConviction = () => {
    const newConviction: Conviction = {
      offenceDate: '',
      convictionDate: '',
      hasFine: '',
      points: '',
      banLength: '',
      code: ''
    };
    onInputChange('convictions', [...data.convictions, newConviction]);
  };

  const removeConviction = (index: number) => {
    const newConvictions = data.convictions.filter((_, i) => i !== index);
    onInputChange('convictions', newConvictions);
  };

  const updateConviction = (index: number, field: keyof Conviction, value: string) => {
    const newConvictions = [...data.convictions];
    newConvictions[index] = { ...newConvictions[index], [field]: value };
    onInputChange('convictions', newConvictions);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-6">
        <Shield className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold">Accidents, Claims & Convictions</h2>
      </div>

      {/* Accidents Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Your Accidents</h3>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Have you had any motor accidents, claims or losses in the past 3 years, no matter who was at fault or if a claim was made?
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="Yes"
                checked={data.hasAccidents === 'Yes'}
                onChange={(e) => onInputChange('hasAccidents', e.target.value)}
                className="rounded-full h-4 w-4 text-indigo-600"
                required
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="No"
                checked={data.hasAccidents === 'No'}
                onChange={(e) => onInputChange('hasAccidents', e.target.value)}
                className="rounded-full h-4 w-4 text-indigo-600"
                required
              />
              <span className="ml-2">No</span>
            </label>
          </div>
        </div>

        {data.hasAccidents === 'Yes' && (
          <div className="space-y-4">
            {data.accidents.map((accident, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium text-gray-900">Accident/Claim {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeAccident(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      When did the incident happen?
                    </label>
                    <input
                      type="date"
                      value={accident.date}
                      onChange={(e) => updateAccident(index, 'date', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Who was at fault?
                    </label>
                    <select
                      value={accident.fault}
                      onChange={(e) => updateAccident(index, 'fault', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select fault</option>
                      <option value="You">You</option>
                      <option value="Other party">Other party</option>
                      <option value="Both parties">Both parties</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      What kind of incident or claim was it?
                    </label>
                    <select
                      value={accident.type}
                      onChange={(e) => updateAccident(index, 'type', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select type</option>
                      <option value="Accident">Accident</option>
                      <option value="Theft">Theft</option>
                      <option value="Windscreen">Windscreen</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {accident.type === 'Other' && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Specify type
                      </label>
                      <select
                        value={accident.subType}
                        onChange={(e) => updateAccident(index, 'subType', e.target.value)}
                        className="w-full p-2 border rounded"
                        required
                      >
                        <option value="">Select type</option>
                        <option value="Fire">Fire</option>
                        <option value="Malicious">Malicious</option>
                        <option value="Storm">Storm</option>
                        <option value="Riot">Riot</option>
                        <option value="Vandalism">Vandalism</option>
                      </select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Which policy was the incident against?
                    </label>
                    <select
                      value={accident.policy}
                      onChange={(e) => updateAccident(index, 'policy', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select policy</option>
                      <option value="Own">Own</option>
                      <option value="Company">Company</option>
                      <option value="Other">Other</option>
                      <option value="Proposers">Proposers</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Was your no claims discount affected?
                    </label>
                    <select
                      value={accident.noClaimsAffected}
                      onChange={(e) => updateAccident(index, 'noClaimsAffected', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="No-protected">No - protected</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Did the accident cause bodily injury?
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="Yes"
                          checked={accident.bodilyInjury === 'Yes'}
                          onChange={(e) => updateAccident(index, 'bodilyInjury', e.target.value)}
                          className="rounded-full h-4 w-4 text-indigo-600"
                          required
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="No"
                          checked={accident.bodilyInjury === 'No'}
                          onChange={(e) => updateAccident(index, 'bodilyInjury', e.target.value)}
                          className="rounded-full h-4 w-4 text-indigo-600"
                          required
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      What was the total value of the claim?
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                        £
                      </span>
                      <input
                        type="number"
                        value={accident.value}
                        onChange={(e) => updateAccident(index, 'value', e.target.value)}
                        className="w-full p-2 pl-7 border rounded"
                        placeholder="Enter value"
                        min="0"
                        step="0.01"
                        onWheel={(e) => e.currentTarget.blur()}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addAccident}
              className="flex items-center text-indigo-600 hover:text-indigo-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Another Accident/Claim
            </button>
          </div>
        )}
      </div>

      {/* Convictions Section */}
      <div className="space-y-4 pt-6 border-t">
        <h3 className="text-lg font-medium text-gray-900">Your Convictions</h3>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            In the last 5 years has this driver had any motoring convictions, penalty points, been disqualified or is any prosecution pending?
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="Yes"
                checked={data.hasConvictions === 'Yes'}
                onChange={(e) => onInputChange('hasConvictions', e.target.value)}
                className="rounded-full h-4 w-4 text-indigo-600"
                required
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="No"
                checked={data.hasConvictions === 'No'}
                onChange={(e) => onInputChange('hasConvictions', e.target.value)}
                className="rounded-full h-4 w-4 text-indigo-600"
                required
              />
              <span className="ml-2">No</span>
            </label>
          </div>
        </div>

        {data.hasConvictions === 'Yes' && (
          <div className="space-y-4">
            {data.convictions.map((conviction, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium text-gray-900">Conviction {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeConviction(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Offence date
                    </label>
                    <input
                      type="date"
                      value={conviction.offenceDate}
                      onChange={(e) => updateConviction(index, 'offenceDate', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      When did the conviction happen?
                    </label>
                    <input
                      type="date"
                      value={conviction.convictionDate}
                      onChange={(e) => updateConviction(index, 'convictionDate', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Was a fine payable?
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="Yes"
                          checked={conviction.hasFine === 'Yes'}
                          onChange={(e) => updateConviction(index, 'hasFine', e.target.value)}
                          className="rounded-full h-4 w-4 text-indigo-600"
                          required
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="No"
                          checked={conviction.hasFine === 'No'}
                          onChange={(e) => updateConviction(index, 'hasFine', e.target.value)}
                          className="rounded-full h-4 w-4 text-indigo-600"
                          required
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      How many points were received?
                    </label>
                    <select
                      value={conviction.points}
                      onChange={(e) => updateConviction(index, 'points', e.target.value)}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select points</option>
                      <option value="None">None</option>
                      <option value="3">3</option>
                      <option value="5">5</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="4">4</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Was a ban incurred?
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="Yes"
                          checked={conviction.banLength !== ''}
                          onChange={(e) => {
                            if (e.target.checked) {
                              updateConviction(index, 'banLength', '1');
                            }
                          }}
                          className="rounded-full h-4 w-4 text-indigo-600"
                          required
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="No"
                          checked={conviction.banLength === ''}
                          onChange={(e) => {
                            if (e.target.checked) {
                              updateConviction(index, 'banLength', '');
                            }
                          }}
                          className="rounded-full h-4 w-4 text-indigo-600"
                          required
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                    {conviction.banLength !== '' && (
                      <div className="mt-2">
                        <label className="block text-sm text-gray-600 mb-1">
                          Ban length (months)
                        </label>
                        <input
                          type="number"
                          value={conviction.banLength}
                          onChange={(e) => updateConviction(index, 'banLength', e.target.value)}
                          className="w-full p-2 border rounded"
                          min="1"
                          onWheel={(e) => e.currentTarget.blur()}
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      DVLA offence code
                    </label>
                    <input
                      type="text"
                      value={conviction.code}
                      onChange={(e) => updateConviction(index, 'code', e.target.value.toUpperCase())}
                      className="w-full p-2 border rounded uppercase"
                      pattern="[A-Za-z0-9]+"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addConviction}
              className="flex items-center text-indigo-600 hover:text-indigo-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Another Conviction
            </button>
          </div>
        )}
      </div>
    </div>
  );
}